import { useBlocker } from 'react-router';
import { useEffect, useRef } from 'react';

import { useConfirmDialog } from './use-confirm-dialog';
import { useSubmitting } from './use-submitting';

import { MESSAGES } from '@/lib/constants';

/**
 * Hook to show browser popup when user tries to leave page with unsaved changes
 * Only blocks:
 * 1. Browser back/forward buttons
 * 2. Header link clicks
 * 3. Navigation buttons like "会社一覧に戻る" (when allowNavigation=false)
 *
 * Does NOT block:
 * - Action buttons with allowNavigation=true (edit, delete, cancel, submit)
 * - Form submissions
 *
 * @param hasUnsavedChanges - Boolean indicating if there are unsaved changes
 */
export const useBeforeUnloadWarning = (hasUnsavedChanges: boolean) => {
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  const { setConfirmDialog } = useConfirmDialog();
  const { isSubmitting, setIsSubmitting } = useSubmitting();
  // Update ref when value changes
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  // Block React Router navigation ONLY when NOT from action button/form
  const blocker = useBlocker(() => {
    // Block only if: has unsaved changes,
    return hasUnsavedChanges;
  });

  // Show confirmation dialog when navigation is blocked
  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (isSubmitting) {
        blocker.proceed();
        setIsSubmitting(false);
      } else {
        setConfirmDialog({
          title: MESSAGES.CONFIRM_LEAVE_PAGE_UNSAVED_CHANGES,
          confirmDialog: () => {
            // User confirmed, proceed with navigation and reset
            blocker.proceed();
          },
          confirmButtonText: '破棄',
          cancelButtonText: 'キャンセル',
          closeDialog: () => {
            // User cancelled, reset blocker
            blocker.reset();
          },
        });
      }
    }
  }, [blocker]);

  // Handle browser beforeunload event (page refresh, close tab, etc.)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChangesRef.current) {
        // Show browser's default unsaved changes dialog
        // Modern browsers use preventDefault() to trigger the dialog
        e.preventDefault();
        // Return value for legacy browser compatibility (ignored by modern browsers)
        return '';
      }
    };

    // Add event listener
    globalThis.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      globalThis.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return blocker;
};
